import React, { useMemo } from 'react';
import { EChart, getCompactGrid, getFocusCartesianGrid, getPreviewGrid } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { EChartsOption } from 'echarts';
import { QUALITY_AXIS_LABELS } from '../helpers';

export interface QualityChartProps {
  data: Array<{ id: string; label: string; value: number; status: string }>;
  height: number | string;
  onBarClick: () => void;
  showDataLabels?: boolean;
  density?: ChartDensity;
}

export function QualityChart({
  data,
  height,
  onBarClick,
  showDataLabels = true,
  density = 'overview',
}: QualityChartProps) {
  const compactGrid = getCompactGrid(true);
  const previewGrid = getPreviewGrid(true);

  const option = useMemo<EChartsOption>(() => {
    const maxValue = Math.max(...data.map((d) => d.value), 1);
    const idToFull = new Map(QUALITY_AXIS_LABELS.map((r) => [r.key, r.full]));
    const shortLabels = data.map((d) => {
      const row = QUALITY_AXIS_LABELS.find((r) => r.key === d.id);
      return row ? row.short : d.label;
    });

    return {
      tooltip:
        density === 'preview'
          ? { show: false }
          : {
              trigger: 'axis',
              formatter: (params: unknown) => {
                const arr = Array.isArray(params) ? params : [params];
                const item = arr[0] as { dataIndex?: number; value?: number } | undefined;
                if (!item) return '';
                const row = data[item.dataIndex ?? 0];
                const full = row ? (idToFull.get(row.id) ?? row.label) : '';
                const value = item.value ?? 0;
                return `<div style="font-weight:600;">${full}</div><div>Count: ${value}</div>`;
              },
            },
      grid:
        density === 'preview'
          ? { ...previewGrid, bottom: 76, containLabel: true }
          : density === 'overview'
            ? { ...compactGrid, bottom: 80, containLabel: true }
            : { ...getFocusCartesianGrid(24), containLabel: true },
      xAxis: {
        type: 'category',
        data: shortLabels,
        axisLabel: {
          interval: 0,
          width: 64,
          overflow: 'break' as const,
          lineHeight: 13,
          fontSize: 10,
          margin: 12,
          align: 'center' as const,
          hideOverlap: false,
          formatter: (val: string) => val,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Count',
        nameLocation: 'middle',
        nameGap: 36,
        axisLabel: { fontSize: 10 },
      },
      series: [{
        type: 'bar',
        data: data.map((d) => d.value),
        label: {
          show: density === 'preview' ? false : showDataLabels && data.length <= 12,
          fontSize: 10,
          position: 'top',
          distance: 6,
          formatter: (params: { value?: unknown }) => {
            const v = typeof params.value === 'number' ? params.value : 0;
            if (v < maxValue * 0.08) return '';
            return String(v);
          },
        },
        labelLayout: { hideOverlap: true },
        barMaxWidth: 36,
        itemStyle: {
          color: (params: { dataIndex: number }) => {
            const s = data[params.dataIndex]?.status;
            if (s === 'fail') return '#EF4444';
            if (s === 'warn') return '#F59E0B';
            return '#22C55E';
          },
        },
      }],
    };
  }, [data, showDataLabels, density, compactGrid, previewGrid]);

  const onEvents = useMemo(() => ({ click: onBarClick }), [onBarClick]);
  return <EChart option={option} height={height} onEvents={onEvents} />;
}
